import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, Button, IconButton, useMediaQuery, Typography, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Alert, Tooltip, Autocomplete } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Add, Delete, DragIndicator, Upload, ArrowBack, Clear, SaveAs, Public, Lock, PictureAsPdf, Image } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { CvCatalogSelect } from '../components/CvCatalogSelect';
import { WizardAccordion } from '../components/WizardAccordion';
import { useAuthorization } from '../hooks/useAuthorization';
import { useActiveCatalog } from '../hooks/useActiveCatalog';
import { en } from '../translations/en';
import { getCVData, saveCVData, CVValidationError } from '../utils/cvApi';
import { uploadProfileImage, getProfileImageUrl, deleteProfileImage } from '../utils/profileImageApi';
import { uploadCvPdf, getCvPdfUrl, deleteCvPdf, uploadCvLogo, getCvLogoUrl, deleteCvLogo } from '../utils/cvAssetsApi';
import { KNOWN_PLATFORMS } from '../components/cv/brandIconRegistry';
import { logger } from '../utils/logger';
import { MarkdownLinkHelp } from '../components/MarkdownLinkHelp';
import { validateMarkdownLinks } from '../utils/safeMarkdown';

// Interface for CV data to track initial state
interface CVDataSnapshot {
  biography: string;
  additionalInfo: string;
  socialMedia: SocialMedia[];
  education: TextEntry[];
  soloExhibitions: TextEntry[];
  groupExhibitions: TextEntry[];
  publications: TextEntry[];
  awards: TextEntry[];
  contact: Contact[];
  collections: Collection[];
  galleryRepresentation: Collection[];
  entryPrivacy: {[key: string]: boolean};
}

interface SocialMedia {
  type: string;
  link: string;
}

interface TextEntry {
  text: string;
}

interface Contact {
  type: string;
  value: string;
}

interface Collection {
  name: string;
}

export const CVEditPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { isCommercialTier } = useAuthorization();
  const isCommercial = isCommercialTier();
  const { activeCatalogOwnerId, activeCatalogAliId, activeOwnerCanEditCv } = useActiveCatalog();
  // PR-4c Tier 2: scope CV reads/writes to the active owned catalog
  // when one is selected. Delegated paths still go through owner_id;
  // catalogAliId only sends when we're on our own catalog.
  const cvCatalogAliId = !activeCatalogOwnerId && activeCatalogAliId ? activeCatalogAliId : undefined;
  // Editing a delegated owner's CV requires the granular canEditCv
  // capability (migration 0035), decoupled from view/full item access.
  // Backend gate: resolve_effective_user_id_for_cv_edit + RLS
  // cv_profiles_write. When acting under a delegated catalog without
  // canEditCv, the CV is read-only: surface that up-front instead of
  // letting the user edit and then 403 on save. activeOwnerCanEditCv
  // aggregates across all of the owner's delegation rows.
  const isDelegatedReadOnly = !!activeCatalogOwnerId && !activeOwnerCanEditCv;
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [cvPdfUrl, setCvPdfUrl] = useState<string | null>(null);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [biography, setBiography] = useState('');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [entryPrivacy, setEntryPrivacy] = useState<{[key: string]: boolean}>({});

  const [socialMedia, setSocialMedia] = useState<SocialMedia[]>([{ type: '', link: '' }]);
  const [education, setEducation] = useState<TextEntry[]>([{ text: '' }]);
  const [soloExhibitions, setSoloExhibitions] = useState<TextEntry[]>([{ text: '' }]);
  const [groupExhibitions, setGroupExhibitions] = useState<TextEntry[]>([{ text: '' }]);
  const [publications, setPublications] = useState<TextEntry[]>([{ text: '' }]);
  const [awards, setAwards] = useState<TextEntry[]>([{ text: '' }]);
  const [contact, setContact] = useState<Contact[]>([{ type: 'Email', value: '' }, { type: 'Phone', value: '' }]);
  const [collections, setCollections] = useState<Collection[]>([{ name: '' }]);
  const [galleryRepresentation, setGalleryRepresentation] = useState<Collection[]>([{ name: '' }]);

  // Drag-to-reorder state: tracks which section/index is being dragged
  const [draggedEntry, setDraggedEntry] = useState<{ section: string; index: number } | null>(null);

  // Track initial state to detect unsaved changes
  const initialDataRef = useRef<CVDataSnapshot | null>(null);
  const hasNavigatedAway = useRef(false);

  // Function to get current state as snapshot
  const getCurrentSnapshot = useCallback((): CVDataSnapshot => ({
    biography,
    additionalInfo,
    socialMedia,
    education,
    soloExhibitions,
    groupExhibitions,
    publications,
    awards,
    contact,
    collections,
    galleryRepresentation,
    entryPrivacy
  }), [biography, additionalInfo, socialMedia, education, soloExhibitions, groupExhibitions, publications, awards, contact, collections, galleryRepresentation, entryPrivacy]);

  // Check if data has changed from initial state
  const isDirty = useCallback((): boolean => {
    if (!initialDataRef.current || loading) return false;
    const current = getCurrentSnapshot();
    return JSON.stringify(current) !== JSON.stringify(initialDataRef.current);
  }, [getCurrentSnapshot, loading]);

  // Dirty-edit guard for the in-page catalog switcher. Returns a promise
  // that resolves to true when the user confirms the discard (or there's
  // nothing to discard), false otherwise. Note: switches via the navbar's
  // CatalogMenuList navigate the user to /my-objects and do NOT route
  // through this guard.
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const discardResolverRef = useRef<((ok: boolean) => void) | null>(null);

  const confirmDiscardIfDirty = useCallback(async (): Promise<boolean> => {
    if (!isDirty()) return true;
    return new Promise<boolean>((resolve) => {
      discardResolverRef.current = resolve;
      setDiscardConfirmOpen(true);
    });
  }, [isDirty]);

  const handleDiscardResolve = (ok: boolean) => {
    setDiscardConfirmOpen(false);
    const resolver = discardResolverRef.current;
    discardResolverRef.current = null;
    if (resolver) resolver(ok);
  };

  // Handle browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty()) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    const loadCVData = async () => {
      try {
        setLoading(true);

        const ownerId = activeCatalogOwnerId ?? undefined;

        // Fetch CV record first so we can read asset-existence flags before
        // issuing the three asset-URL requests.  This prevents browser-console
        // 404 errors for users who have never uploaded a profile image, CV PDF,
        // or logo — the backend sets hasProfileImage/hasCvPdf/hasLogo via
        // lightweight S3 head_object checks.
        const data = await getCVData(ownerId, cvCatalogAliId);

        // Gate each asset fetch on the flag from the CV record.  When the flag
        // is false or absent (e.g. old backend that doesn't set it yet), skip
        // the request entirely and clear any stale state so switching owners
        // never leaves a prior delegator's asset visible.
        const [imageResult, pdfResult, logoResult] = await Promise.allSettled([
          data.hasProfileImage ? getProfileImageUrl(ownerId, cvCatalogAliId) : Promise.resolve(null),
          data.hasCvPdf ? getCvPdfUrl(ownerId, cvCatalogAliId) : Promise.resolve(null),
          data.hasLogo ? getCvLogoUrl(ownerId, cvCatalogAliId) : Promise.resolve(null),
        ]);
        // Setters run unconditionally so switching owners always clears
        // previously-loaded asset state, even when the new owner has none.
        if (imageResult.status === 'fulfilled') {
          setProfileImageUrl(imageResult.value?.imageUrl ?? null);
        }
        if (pdfResult.status === 'fulfilled') {
          setCvPdfUrl(pdfResult.value?.pdfUrl ?? null);
        }
        if (logoResult.status === 'fulfilled') {
          setLogoUrl(logoResult.value?.logoUrl ?? null);
        }
        if (data.biography) setBiography(data.biography);
        if (data.additionalInfo) setAdditionalInfo(data.additionalInfo);
        if (data.socialMedia && data.socialMedia.length > 0) setSocialMedia(data.socialMedia);
        if (data.education && data.education.length > 0) setEducation(data.education);
        if (data.soloExhibitions && data.soloExhibitions.length > 0) {
          setSoloExhibitions(data.soloExhibitions);
        }
        if (data.groupExhibitions && data.groupExhibitions.length > 0) {
          setGroupExhibitions(data.groupExhibitions);
        }
        if (data.publications && data.publications.length > 0) {
          setPublications(data.publications);
        }
        if (data.awards && data.awards.length > 0) {
          setAwards(data.awards);
        }
        if (data.contact && data.contact.length > 0) setContact(data.contact);
        if (data.collections && data.collections.length > 0) {
          setCollections(data.collections);
        }
        if (data.galleryRepresentation && data.galleryRepresentation.length > 0) {
          setGalleryRepresentation(data.galleryRepresentation);
        }
        if (data.entryPrivacy) setEntryPrivacy(data.entryPrivacy);

        // Save initial state snapshot for dirty checking
        initialDataRef.current = {
          biography: data.biography || '',
          additionalInfo: data.additionalInfo || '',
          socialMedia: data.socialMedia && data.socialMedia.length > 0 ? data.socialMedia : [{ type: '', link: '' }],
          education: data.education && data.education.length > 0 ? data.education : [{ text: '' }],
          soloExhibitions: data.soloExhibitions && data.soloExhibitions.length > 0 ? data.soloExhibitions : [{ text: '' }],
          groupExhibitions: data.groupExhibitions && data.groupExhibitions.length > 0 ? data.groupExhibitions : [{ text: '' }],
          publications: data.publications && data.publications.length > 0 ? data.publications : [{ text: '' }],
          awards: data.awards && data.awards.length > 0 ? data.awards : [{ text: '' }],
          contact: data.contact && data.contact.length > 0 ? data.contact : [{ type: 'Email', value: '' }, { type: 'Phone', value: '' }],
          collections: data.collections && data.collections.length > 0 ? data.collections : [{ name: '' }],
          galleryRepresentation: data.galleryRepresentation && data.galleryRepresentation.length > 0 ? data.galleryRepresentation : [{ name: '' }],
          entryPrivacy: data.entryPrivacy || {}
        };
      } catch (err) {
        logger.error('[CVEditPage] Failed to load CV data', err);
        // Surface load failures instead of rendering a silently-blank
        // form. 403 indicates a missing/expired delegation; anything
        // else is a generic load error.
        const status =
          (err as { response?: { status?: number } })?.response?.status ?? null;
        if (status === 403) {
          setError(
            'You do not have permission to view this CV. ' +
              'The delegation may have expired or been revoked.',
          );
        } else {
          setError('Failed to load CV data. Please refresh and try again.');
        }
      } finally {
        setLoading(false);
      }
    };
    loadCVData();
  }, [activeCatalogOwnerId, activeCatalogAliId]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    // dataTransfer is always present in real browsers but may be absent under
    // jsdom (fireEvent.dragOver) — guard so the drag handlers stay testable.
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragStart = (section: string, index: number) => {
    setDraggedEntry({ section, index });
  };

  const handleDrop = (section: string, dropIndex: number) => {
    if (!draggedEntry) return;
    if (draggedEntry.section !== section) {
      // Cross-section drop: no-op
      setDraggedEntry(null);
      return;
    }
    const { index: dragIndex } = draggedEntry;
    if (dragIndex === dropIndex) {
      setDraggedEntry(null);
      return;
    }

    // Helper: reorder an array by moving item from fromIdx to toIdx
    const reorderArray = <T,>(arr: T[], fromIdx: number, toIdx: number): T[] => {
      const result = [...arr];
      const [removed] = result.splice(fromIdx, 1);
      result.splice(toIdx, 0, removed);
      return result;
    };

    // Helper: remap entryPrivacy keys for a section after a reorder
    const remapPrivacy = (
      prev: { [key: string]: boolean },
      sec: string,
      length: number,
      fromIdx: number,
      toIdx: number
    ): { [key: string]: boolean } => {
      // Extract the per-entry privacy values (indexed 0..length-1)
      const values: boolean[] = Array.from({ length }, (_, i) => prev[`${sec}-${i}`] ?? false);
      const reordered = reorderArray(values, fromIdx, toIdx);
      // Build updated entries, removing old keys and writing new ones
      const updated = { ...prev };
      reordered.forEach((val, i) => {
        updated[`${sec}-${i}`] = val;
      });
      return updated;
    };

    // Map section name to its list and setter
    type AnyEntry = SocialMedia | TextEntry | Contact | Collection;
    const sectionMap: Record<
      string,
      { list: AnyEntry[]; setList: React.Dispatch<React.SetStateAction<AnyEntry[]>> }
    > = {
      socialMedia: { list: socialMedia as AnyEntry[], setList: setSocialMedia as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      education: { list: education as AnyEntry[], setList: setEducation as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      soloExhibitions: { list: soloExhibitions as AnyEntry[], setList: setSoloExhibitions as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      groupExhibitions: { list: groupExhibitions as AnyEntry[], setList: setGroupExhibitions as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      publications: { list: publications as AnyEntry[], setList: setPublications as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      awards: { list: awards as AnyEntry[], setList: setAwards as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      contact: { list: contact as AnyEntry[], setList: setContact as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      collections: { list: collections as AnyEntry[], setList: setCollections as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
      galleryRepresentation: { list: galleryRepresentation as AnyEntry[], setList: setGalleryRepresentation as React.Dispatch<React.SetStateAction<AnyEntry[]>> },
    };

    const entry = sectionMap[section];
    if (!entry) {
      setDraggedEntry(null);
      return;
    }

    const { list, setList } = entry;
    setList(reorderArray(list, dragIndex, dropIndex) as typeof list);
    setEntryPrivacy((prev) => remapPrivacy(prev, section, list.length, dragIndex, dropIndex));
    setDraggedEntry(null);
  };

  const setAllPrivacy = (isPublic: boolean) => {
    // Get all possible entry keys for all sections
    const allEntryKeys: string[] = [];

    // Add all current entries
    contact.forEach((_, index) => allEntryKeys.push(`contact-${index}`));
    socialMedia.forEach((_, index) => allEntryKeys.push(`socialMedia-${index}`));
    education.forEach((_, index) => allEntryKeys.push(`education-${index}`));
    soloExhibitions.forEach((_, index) => allEntryKeys.push(`soloExhibitions-${index}`));
    groupExhibitions.forEach((_, index) => allEntryKeys.push(`groupExhibitions-${index}`));
    publications.forEach((_, index) => allEntryKeys.push(`publications-${index}`));
    awards.forEach((_, index) => allEntryKeys.push(`awards-${index}`));
    collections.forEach((_, index) => allEntryKeys.push(`collections-${index}`));
    galleryRepresentation.forEach((_, index) => allEntryKeys.push(`galleryRepresentation-${index}`));
    allEntryKeys.push('biography-0'); // Biography is single entry
    allEntryKeys.push('cvPdf');
    allEntryKeys.push('logo');

    // Set all entries to the same privacy setting
    const updates: {[key: string]: boolean} = {};
    allEntryKeys.forEach(key => {
      updates[key] = isPublic;
    });
    setEntryPrivacy(updates);
  };

  const toggleEntryPrivacy = (entryKey: string) => {
    setEntryPrivacy(prev => ({
      ...prev,
      [entryKey]: !prev[entryKey] // false = private (default), true = public
    }));
  };



  const EntryPrivacyToggle = ({ entryKey }: { entryKey: string }) => {
    const isPublic = entryPrivacy[entryKey] ?? false;
    return (
      <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ ml: 1 }}>
        <Tooltip title={isPublic ? 'Public - Click to make private' : 'Private - Click to make public'} arrow>
          <IconButton
            onClick={() => toggleEntryPrivacy(entryKey)}
            size="small"
            sx={{
              color: isPublic ? 'success.main' : 'text.secondary',
              '&:hover': {
                backgroundColor: isPublic ? 'success.light' : 'action.hover',
              },
            }}
          >
            {isPublic ? <Public sx={{ fontSize: 18 }} /> : <Lock sx={{ fontSize: 18 }} />}
          </IconButton>
        </Tooltip>
        <Typography
          variant="caption"
          sx={{
            color: isPublic ? 'success.main' : 'text.secondary',
            fontWeight: 500,
          }}
        >
          {isPublic ? 'Public' : 'Private'}
        </Typography>
      </Box>
    );
  };

  // Social Media handlers
  const addSocialMedia = () => setSocialMedia([...socialMedia, { type: '', link: '' }]);
  const removeSocialMedia = (index: number) => {
    if (socialMedia.length > 1) setSocialMedia(socialMedia.filter((_, i) => i !== index));
  };
  const updateSocialMedia = (index: number, field: keyof SocialMedia, value: string) => {
    setSocialMedia(socialMedia.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Validate `[label](url)` Markdown links in a field value. On a bad URL,
  // set/update the key in fieldErrors; on valid input, clear it. This reuses the
  // shared regex/allowlist in utils/safeMarkdown — same check runs on the backend.
  const validateLinkField = useCallback((key: string, value: string) => {
    const result = validateMarkdownLinks(value);
    setFieldErrors(prev => {
      const next = { ...prev };
      if (result.ok) {
        delete next[key];
      } else {
        next[key] = `Only http:// or https:// links are allowed. Found: ${result.badUrl}`;
      }
      return next;
    });
  }, [setFieldErrors]);

  // Education handlers
  const addEducation = () => setEducation([...education, { text: '' }]);
  const removeEducation = (index: number) => {
    if (education.length > 1) setEducation(education.filter((_, i) => i !== index));
  };
  const updateEducation = (index: number, value: string) => {
    setEducation(education.map((item, i) => i === index ? { ...item, text: value } : item));
  };

  // Solo Exhibitions handlers
  const addSoloExhibition = () => setSoloExhibitions([...soloExhibitions, { text: '' }]);
  const removeSoloExhibition = (index: number) => {
    if (soloExhibitions.length > 1) setSoloExhibitions(soloExhibitions.filter((_, i) => i !== index));
  };
  const updateSoloExhibition = (index: number, value: string) => {
    setSoloExhibitions(soloExhibitions.map((item, i) => i === index ? { ...item, text: value } : item));
  };

  // Group Exhibitions handlers
  const addGroupExhibition = () => setGroupExhibitions([...groupExhibitions, { text: '' }]);
  const removeGroupExhibition = (index: number) => {
    if (groupExhibitions.length > 1) setGroupExhibitions(groupExhibitions.filter((_, i) => i !== index));
  };
  const updateGroupExhibition = (index: number, value: string) => {
    setGroupExhibitions(groupExhibitions.map((item, i) => i === index ? { ...item, text: value } : item));
  };

  // Publications handlers
  const addPublication = () => setPublications([...publications, { text: '' }]);
  const removePublication = (index: number) => {
    if (publications.length > 1) setPublications(publications.filter((_, i) => i !== index));
  };
  const updatePublication = (index: number, value: string) => {
    setPublications(publications.map((item, i) => i === index ? { ...item, text: value } : item));
  };

  // Awards handlers
  const addAward = () => setAwards([...awards, { text: '' }]);
  const removeAward = (index: number) => {
    if (awards.length > 1) setAwards(awards.filter((_, i) => i !== index));
  };
  const updateAward = (index: number, value: string) => {
    setAwards(awards.map((item, i) => i === index ? { ...item, text: value } : item));
  };

  // Contact handlers
  const addContact = () => setContact([...contact, { type: '', value: '' }]);
  const removeContact = (index: number) => {
    if (contact.length > 1) setContact(contact.filter((_, i) => i !== index));
  };
  const updateContact = (index: number, field: keyof Contact, value: string) => {
    setContact(contact.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    ));
  };

  // Collections handlers
  const addCollection = () => setCollections([...collections, { name: '' }]);
  const removeCollection = (index: number) => {
    if (collections.length > 1) setCollections(collections.filter((_, i) => i !== index));
  };
  const updateCollection = (index: number, value: string) => {
    setCollections(collections.map((item, i) =>
      i === index ? { ...item, name: value } : item
    ));
  };

  // Gallery Representation handlers
  const addGalleryRep = () => setGalleryRepresentation([...galleryRepresentation, { name: '' }]);
  const removeGalleryRep = (index: number) => {
    if (galleryRepresentation.length > 1) setGalleryRepresentation(galleryRepresentation.filter((_, i) => i !== index));
  };
  const updateGalleryRep = (index: number, value: string) => {
    setGalleryRepresentation(galleryRepresentation.map((item, i) =>
      i === index ? { ...item, name: value } : item
    ));
  };

  // Clear all and save functions
  const handleClearAll = () => {
    setBiography('');
    setAdditionalInfo('');
    setSocialMedia([{ type: '', link: '' }]);
    setEducation([{ text: '' }]);
    setSoloExhibitions([{ text: '' }]);
    setGroupExhibitions([{ text: '' }]);
    setPublications([{ text: '' }]);
    setAwards([{ text: '' }]);
    setContact([{ type: 'Email', value: '' }, { type: 'Phone', value: '' }]);
    setCollections([{ name: '' }]);
    setGalleryRepresentation([{ name: '' }]);
    setEntryPrivacy({});
    setFieldErrors({});
  };

  const handleSaveChanges = () => {
    setSaveModalOpen(true);
  };

  const confirmSave = async () => {
    const cvData = {
      biography,
      additionalInfo,
      socialMedia,
      education,
      soloExhibitions,
      groupExhibitions,
      publications,
      awards,
      contact,
      collections,
      galleryRepresentation,
      entryPrivacy
    };
    try {
      setSaving(true);
      setError(null);
      setFieldErrors({});
      await saveCVData(cvData, activeCatalogOwnerId ?? undefined, cvCatalogAliId);
      setSaveModalOpen(false);
      // Update initial state to match saved data (no longer dirty)
      initialDataRef.current = getCurrentSnapshot();
      hasNavigatedAway.current = true;
      navigate('/curriculum-vitae');
    } catch (err) {
      logger.error('[CVEditPage] Save failed', err);
      setSaveModalOpen(false);

      // Handle validation errors with field-specific messages
      if (err instanceof CVValidationError) {
        const newFieldErrors: Record<string, string> = {};
        for (const fieldError of err.fieldErrors) {
          // Convert to key format: section-index-field
          const key = fieldError.index !== null
            ? `${fieldError.section}-${fieldError.index}-${fieldError.fieldName}`
            : fieldError.fieldName;
          newFieldErrors[key] = fieldError.message;
        }
        setFieldErrors(newFieldErrors);

        // Show summary error
        const errorCount = err.fieldErrors.length;
        setError(`Validation failed: ${errorCount} field${errorCount > 1 ? 's have' : ' has'} invalid values. Please correct the highlighted fields.`);
      } else {
        const status =
          (err as { response?: { status?: number } })?.response?.status ?? null;
        if (status === 403) {
          // Reachable when a view-only delegate slips past the in-page
          // dropdown, or when a delegation expires mid-session. The
          // message points the user at the real fix (asking the owner
          // for Full access) rather than the generic "try again".
          setError(
            "You don't have edit permission for this CV. " +
              'Ask the catalog owner to grant Full access.',
          );
        } else {
          setError('Failed to save CV data. Please try again.');
        }
      }
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {isDelegatedReadOnly && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="cv-readonly-banner">
          You have view-only access to this CV. Ask the catalog owner to grant
          Full access to make changes.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton
            onClick={() => navigate('/curriculum-vitae')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              color: theme.palette.primary.main,
              fontFamily: 'Libre Baskerville, serif',
              fontWeight: 'bold'
            }}
          >
            {isCommercial ? en.profile.commercial.editAbout : en.profile.editCurriculumVitae}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <CvCatalogSelect onBeforeSwitch={confirmDiscardIfDirty} />
          <Button
            variant="outlined"
            startIcon={<Lock />}
            onClick={() => setAllPrivacy(false)}
            size="small"
          >
            All Private
          </Button>
          <Button
            variant="outlined"
            startIcon={<Public />}
            onClick={() => setAllPrivacy(true)}
            size="small"
          >
            All Public
          </Button>
          <Button variant="outlined" startIcon={<Clear />} onClick={handleClearAll}>
            {en.profile.clearAll}
          </Button>
          <Tooltip
            title={
              isDelegatedReadOnly
                ? 'View-only access — ask the owner for Full access to edit this CV'
                : Object.keys(fieldErrors).length > 0
                  ? 'Fix highlighted link errors before saving'
                  : ''
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<SaveAs sx={{ color: 'white' }} />}
                onClick={handleSaveChanges}
                disabled={isDelegatedReadOnly || Object.keys(fieldErrors).length > 0}
              >
                {en.profile.saveChanges}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* All 10 accordions - same as before */}
      <WizardAccordion title={en.profile.profilePicture} subtitle="Upload your profile photo" defaultExpanded={true}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {profileImageUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={profileImageUrl}
                alt="Profile"
                sx={{
                  width: 150,
                  height: 150,
                  borderRadius: 2,
                  objectFit: 'cover',
                  border: `2px solid ${theme.palette.divider}`
                }}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  try {
                    await deleteProfileImage(activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                    setProfileImageUrl(null);
                  } catch (error) {
                    logger.error('Failed to delete profile image', error);
                    setError('Failed to delete profile image');
                  }
                }}
              >
                Remove
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={uploadingImage ? <CircularProgress size={20} /> : <Upload />}
              component="label"
              disabled={uploadingImage}
            >
              {profileImageUrl ? 'Change Photo' : en.profile.uploadPhoto}
              <input
                type="file"
                hidden
                accept="image/*,.heic,.heif,image/heic,image/heif"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      setUploadingImage(true);
                      setError(null);
                      const result = await uploadProfileImage(file, activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                      setProfileImageUrl(result.imageUrl);
                    } catch (error) {
                      logger.error('Failed to upload profile image', error);
                      setError('Failed to upload profile image');
                    } finally {
                      setUploadingImage(false);
                    }
                  }
                }}
              />
            </Button>
            {uploadingImage && <Typography variant="caption">Uploading...</Typography>}
          </Box>
        </Box>
      </WizardAccordion>

      <WizardAccordion title="Artist Logo" subtitle="Upload your logo to display on your CV and PDF exports. PNG, JPEG, or SVG (max 5MB)." defaultExpanded={true}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {logoUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={logoUrl}
                alt="Artist Logo"
                sx={{
                  width: 120,
                  height: 120,
                  objectFit: 'contain',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  p: 1,
                  backgroundColor: theme.palette.background.default,
                }}
              />
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  try {
                    await deleteCvLogo(activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                    setLogoUrl(null);
                  } catch (err) {
                    logger.error('Failed to delete logo', err);
                    setError('Failed to delete logo');
                  }
                }}
              >
                Remove
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={uploadingLogo ? <CircularProgress size={20} /> : <Image />}
              component="label"
              disabled={uploadingLogo}
            >
              {logoUrl ? 'Change Logo' : 'Upload Logo'}
              <input
                type="file"
                hidden
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      setUploadingLogo(true);
                      setError(null);
                      const result = await uploadCvLogo(file, activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                      setLogoUrl(result.logoUrl);
                    } catch (err) {
                      logger.error('Failed to upload logo', err);
                      setError(err instanceof Error ? err.message : 'Failed to upload logo');
                    } finally {
                      setUploadingLogo(false);
                    }
                  }
                }}
              />
            </Button>
            <EntryPrivacyToggle entryKey="logo" />
            {uploadingLogo && <Typography variant="caption">Uploading...</Typography>}
          </Box>
        </Box>
      </WizardAccordion>

      <WizardAccordion title="CV PDF Upload" subtitle="Upload your existing CV as a PDF for others to download (max 20MB). This is an alternative to entering your CV details below." defaultExpanded={true}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {cvPdfUrl && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PictureAsPdf sx={{ color: theme.palette.error.main, fontSize: 40 }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>CV PDF uploaded</Typography>
                  <Typography
                    variant="caption"
                    component="a"
                    href={cvPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: theme.palette.primary.main }}
                  >
                    Preview PDF
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={async () => {
                  try {
                    await deleteCvPdf(activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                    setCvPdfUrl(null);
                  } catch (err) {
                    logger.error('Failed to delete CV PDF', err);
                    setError('Failed to delete CV PDF');
                  }
                }}
              >
                Remove
              </Button>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={uploadingPdf ? <CircularProgress size={20} /> : <PictureAsPdf />}
              component="label"
              disabled={uploadingPdf}
            >
              {cvPdfUrl ? 'Replace PDF' : 'Upload PDF'}
              <input
                type="file"
                hidden
                accept=".pdf,application/pdf"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    try {
                      setUploadingPdf(true);
                      setError(null);
                      const result = await uploadCvPdf(file, activeCatalogOwnerId ?? undefined, cvCatalogAliId);
                      setCvPdfUrl(result.pdfUrl);
                    } catch (err) {
                      logger.error('Failed to upload CV PDF', err);
                      setError(err instanceof Error ? err.message : 'Failed to upload CV PDF');
                    } finally {
                      setUploadingPdf(false);
                    }
                  }
                }}
              />
            </Button>
            <EntryPrivacyToggle entryKey="cvPdf" />
            {uploadingPdf && <Typography variant="caption">Uploading...</Typography>}
          </Box>
        </Box>
      </WizardAccordion>

      <WizardAccordion title="Contact Information" subtitle="Add your email and phone number to allow others to contact you. Optional." defaultExpanded={!isMobile}>
        <Box>
          {contact.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('contact', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('contact', index); }}>
              <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
              <Autocomplete
                fullWidth
                freeSolo
                options={KNOWN_PLATFORMS}
                value={item.type}
                onInputChange={(_, newValue) => updateContact(index, 'type', newValue ?? '')}
                renderInput={(params) => (
                  <TextField {...params} label={`Contact Type ${index + 1}`} variant="outlined" />
                )}
              />
              <TextField
                fullWidth
                label={`Contact Value ${index + 1}`}
                value={item.value}
                onChange={(e) => updateContact(index, 'value', e.target.value)}
                variant="outlined"
                type={item.type?.toLowerCase() === 'email' ? 'email' : 'text'}
              />
              <EntryPrivacyToggle entryKey={`contact-${index}`} />
              {contact.length > 1 && (
                <IconButton onClick={() => removeContact(index)} color="error" size="small">
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          <Button startIcon={<Add />} onClick={addContact} variant="outlined" size="small">Add Contact</Button>
        </Box>
      </WizardAccordion>

      <WizardAccordion title={isCommercial ? en.profile.commercial.aboutUs : en.profile.biography} subtitle={isCommercial ? en.profile.commercial.aboutUsSubtitle : en.profile.biographySubtitle} defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
        <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
            <TextField
              fullWidth
              multiline
              minRows={8}
              maxRows={20}
              label={en.profile.biography}
              variant="outlined"
              value={biography}
              onChange={(e) => { setBiography(e.target.value); validateLinkField('biography', e.target.value); }}
              error={Boolean(fieldErrors['biography'])}
              helperText={fieldErrors['biography']}
            />
            <EntryPrivacyToggle entryKey="biography-0" />
          </Box>
        </Box>
      </WizardAccordion>

      <WizardAccordion title={en.profile.socialMedia} subtitle={en.profile.socialMediaSubtitle} defaultExpanded={!isMobile}>
        <Box>
          {socialMedia.map((item, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('socialMedia', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('socialMedia', index); }}>
              <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
              <Autocomplete
                fullWidth
                freeSolo
                options={KNOWN_PLATFORMS}
                value={item.type}
                onInputChange={(_, newValue) => updateSocialMedia(index, 'type', newValue ?? '')}
                renderInput={(params) => (
                  <TextField {...params} label={`${en.profile.socialMediaType} ${index + 1}`} variant="outlined" />
                )}
              />
              <TextField fullWidth label={`${en.profile.link} ${index + 1}`} value={item.link} onChange={(e) => updateSocialMedia(index, 'link', e.target.value)} variant="outlined" />
              <EntryPrivacyToggle entryKey={`socialMedia-${index}`} />
              {socialMedia.length > 1 && (
                <IconButton onClick={() => removeSocialMedia(index)} color="error" size="small">
                  <Delete />
                </IconButton>
              )}
            </Box>
          ))}
          <Button startIcon={<Add />} onClick={addSocialMedia} variant="outlined" size="small">{en.profile.addSocialMedia}</Button>
        </Box>
      </WizardAccordion>

      {/* Creator-only sections - hidden for commercial tier */}
      {!isCommercial && (
        <>
          <WizardAccordion title="Education" subtitle="List academic degrees, certificates, or other relevant training. You can add multiple entries." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {education.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('education', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('education', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Education ${index + 1}`}
                    value={item.text}
                    onChange={(e) => { updateEducation(index, e.target.value); validateLinkField(`education-${index}-text`, e.target.value); }}
                    variant="outlined"
                    placeholder="e.g. BFA, Art School, 2010–2014"
                    error={Boolean(fieldErrors[`education-${index}-text`])}
                    helperText={fieldErrors[`education-${index}-text`]}
                  />
                  <EntryPrivacyToggle entryKey={`education-${index}`} />
                  {education.length > 1 && (
                    <IconButton onClick={() => removeEducation(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addEducation} variant="outlined" size="small">Add Education</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Solo Exhibitions" subtitle="List exhibitions where you were the sole or primary artist. Include dates, venues, and locations. Optional." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {soloExhibitions.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('soloExhibitions', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('soloExhibitions', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Solo Exhibition ${index + 1}`}
                    value={item.text}
                    onChange={(e) => { updateSoloExhibition(index, e.target.value); validateLinkField(`soloExhibitions-${index}-text`, e.target.value); }}
                    variant="outlined"
                    placeholder="e.g. 2022, Solo Show at Gallery X, New York"
                    error={Boolean(fieldErrors[`soloExhibitions-${index}-text`])}
                    helperText={fieldErrors[`soloExhibitions-${index}-text`]}
                  />
                  <EntryPrivacyToggle entryKey={`soloExhibitions-${index}`} />
                  {soloExhibitions.length > 1 && (
                    <IconButton onClick={() => removeSoloExhibition(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addSoloExhibition} variant="outlined" size="small">Add Solo Exhibition</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Group Exhibitions" subtitle="List exhibitions with multiple artists, including dates, venues, and locations. Optional." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {groupExhibitions.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('groupExhibitions', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('groupExhibitions', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Group Exhibition ${index + 1}`}
                    value={item.text}
                    onChange={(e) => { updateGroupExhibition(index, e.target.value); validateLinkField(`groupExhibitions-${index}-text`, e.target.value); }}
                    variant="outlined"
                    placeholder="e.g. 2021, Group Show at Museum Y, London"
                    error={Boolean(fieldErrors[`groupExhibitions-${index}-text`])}
                    helperText={fieldErrors[`groupExhibitions-${index}-text`]}
                  />
                  <EntryPrivacyToggle entryKey={`groupExhibitions-${index}`} />
                  {groupExhibitions.length > 1 && (
                    <IconButton onClick={() => removeGroupExhibition(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addGroupExhibition} variant="outlined" size="small">Add Group Exhibition</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Publications" subtitle="Record articles, catalogs, books, or media mentions that feature your work. Optional." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {publications.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('publications', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('publications', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Publication ${index + 1}`}
                    value={item.text}
                    onChange={(e) => { updatePublication(index, e.target.value); validateLinkField(`publications-${index}-text`, e.target.value); }}
                    variant="outlined"
                    placeholder="e.g. 2020, Art Review Magazine, 'New Talent'"
                    error={Boolean(fieldErrors[`publications-${index}-text`])}
                    helperText={fieldErrors[`publications-${index}-text`]}
                  />
                  <EntryPrivacyToggle entryKey={`publications-${index}`} />
                  {publications.length > 1 && (
                    <IconButton onClick={() => removePublication(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addPublication} variant="outlined" size="small">Add Publication</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Awards & Honors" subtitle="List any awards, prizes, grants, or recognitions you have received. Optional." defaultExpanded={!isMobile}>
            <Box>
              {awards.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('awards', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('awards', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Award / Honor ${index + 1}`}
                    value={item.text}
                    onChange={(e) => { updateAward(index, e.target.value); validateLinkField(`awards-${index}-text`, e.target.value); }}
                    variant="outlined"
                    placeholder="e.g. 2019, Best New Artist, Foundation Z"
                    error={Boolean(fieldErrors[`awards-${index}-text`])}
                    helperText={fieldErrors[`awards-${index}-text`]}
                  />
                  <EntryPrivacyToggle entryKey={`awards-${index}`} />
                  {awards.length > 1 && (
                    <IconButton onClick={() => removeAward(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addAward} variant="outlined" size="small">Add Award</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Collections" subtitle="List public institutions (museums, foundations) or private collections (individuals or corporations) where your work is held. Both are visible to the public. Optional." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {collections.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('collections', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('collections', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Collection ${index + 1}`}
                    value={item.name}
                    onChange={(e) => { updateCollection(index, e.target.value); validateLinkField(`collections-${index}-name`, e.target.value); }}
                    variant="outlined"
                    error={Boolean(fieldErrors[`collections-${index}-name`])}
                    helperText={fieldErrors[`collections-${index}-name`]}
                  />
                  <EntryPrivacyToggle entryKey={`collections-${index}`} />
                  {collections.length > 1 && (
                    <IconButton onClick={() => removeCollection(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addCollection} variant="outlined" size="small">Add Collection</Button>
            </Box>
          </WizardAccordion>

          <WizardAccordion title="Gallery Representation" subtitle={en.profile.galleryRepresentationSubtitle} defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
            <Box>
              {galleryRepresentation.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }} draggable onDragStart={() => handleDragStart('galleryRepresentation', index)} onDragOver={handleDragOver} onDrop={(e) => { e.preventDefault(); handleDrop('galleryRepresentation', index); }}>
                  <DragIndicator sx={{ color: theme.palette.text.disabled, cursor: 'grab' }} />
                  <TextField
                    fullWidth
                    label={`Gallery ${index + 1}`}
                    value={item.name}
                    onChange={(e) => { updateGalleryRep(index, e.target.value); validateLinkField(`galleryRepresentation-${index}-name`, e.target.value); }}
                    variant="outlined"
                    error={Boolean(fieldErrors[`galleryRepresentation-${index}-name`])}
                    helperText={fieldErrors[`galleryRepresentation-${index}-name`]}
                  />
                  <EntryPrivacyToggle entryKey={`galleryRepresentation-${index}`} />
                  {galleryRepresentation.length > 1 && (
                    <IconButton onClick={() => removeGalleryRep(index)} color="error" size="small">
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Button startIcon={<Add />} onClick={addGalleryRep} variant="outlined" size="small">Add Gallery</Button>
            </Box>
          </WizardAccordion>
        </>
      )}

      <WizardAccordion title="Additional Information" subtitle="Add any other relevant information about your artistic practice, achievements, or background. Optional." defaultExpanded={!isMobile} titleAdornment={<MarkdownLinkHelp />}>
        <Box sx={{ p: 1, border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1, gap: 1 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Additional Information"
              variant="outlined"
              placeholder="Enter any additional information about your artistic practice, achievements, or background..."
              value={additionalInfo}
              onChange={(e) => { setAdditionalInfo(e.target.value); validateLinkField('additionalInfo', e.target.value); }}
              error={Boolean(fieldErrors['additionalInfo'])}
              helperText={fieldErrors['additionalInfo']}
            />
            <EntryPrivacyToggle entryKey="additionalInfo-0" />
          </Box>
        </Box>
      </WizardAccordion>

      {/* Save Confirmation Modal */}
      <Dialog open={saveModalOpen} onClose={() => !saving && setSaveModalOpen(false)}>
        <DialogTitle>{en.profile.saveChangesTitle}</DialogTitle>
        <DialogContent>
          <Typography>{en.profile.saveChangesMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveModalOpen(false)} disabled={saving}>{en.profile.no}</Button>
          <Button onClick={confirmSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={24} /> : en.profile.yes}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={discardConfirmOpen}
        onClose={() => handleDiscardResolve(false)}
        aria-labelledby="cv-discard-title"
      >
        <DialogTitle id="cv-discard-title">Discard unsaved changes?</DialogTitle>
        <DialogContent>
          <Typography>
            You have unsaved edits on this CV. Switching to another catalog will lose them.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDiscardResolve(false)} autoFocus>
            Cancel
          </Button>
          <Button onClick={() => handleDiscardResolve(true)} color="error" variant="contained">
            Discard
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};
